import React, { useState, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green, amber } from "@material-ui/core/colors";
import {
	Button,
	IconButton,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Box,
	Switch,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Refresh,
	Warning,
	Info,
	Description,
	PauseCircleFilled,
} from "@material-ui/icons";
import Chip from "@material-ui/core/Chip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import MetaTemplatesDrawer from "../../components/MetaTemplatesDrawer";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import MetaHealthBanner from "../../components/MetaHealthBanner";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	alertBanner: {
		padding: "8px 14px",
		marginBottom: 8,
		borderRadius: 6,
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	// feat_204 — rediseño a tarjetas
	cardsWrap: {
		flex: 1,
		overflowY: "auto",
		...theme.scrollbarStyles,
		padding: theme.spacing(1),
	},
	card: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		borderRadius: 10,
		display: "flex",
		flexDirection: "column",
		gap: 10,
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 10,
		flexWrap: "wrap",
	},
	headerLeft: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
	nameRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
	cardName: { fontWeight: 700, fontSize: 15, lineHeight: 1.2 },
	cardSub: { fontSize: 12, color: "#6b7280", marginTop: 2 },
	cardActions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
	emptyState: { textAlign: "center", color: "#9ca3af", padding: theme.spacing(4) },
	// Switch de envío por conexión: una conexión pausada tiene que saltar a la
	// vista (es un estado peligroso de olvidar) — borde rojo grueso + franja.
	cardPaused: {
		border: "2px solid #dc2626",
		backgroundColor: "rgba(220, 38, 38, 0.06)",
	},
	pausedChip: {
		backgroundColor: "#dc2626",
		color: "#fff",
		fontWeight: 800,
		fontSize: "0.7rem",
		letterSpacing: 0.5,
	},
	pausedStrip: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "8px 12px",
		borderRadius: 6,
		backgroundColor: "#dc2626",
		color: "#fff",
		fontWeight: 700,
		fontSize: 13,
	},
	outboundToggle: { display: "flex", alignItems: "center", gap: 4 },
	outboundLabel: { fontSize: 13, fontWeight: 600 },
}));

const TYPE_INFO = {
	whaileys: { label: "QR — Pro", color: "#10b981", bg: "#d1fae5" },
	wwebjs: { label: "QR — Web", color: "#3b82f6", bg: "#dbeafe" },
};

const getTypeChip = (provider) => {
	const t = TYPE_INFO[provider] || { label: provider, color: "#6b7280", bg: "#f3f4f6" };
	return (
		<Chip
			label={t.label}
			size="small"
			style={{ backgroundColor: t.bg, color: t.color, fontWeight: 700, fontSize: "0.65rem" }}
		/>
	);
};

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();
	return (
		<Tooltip
			arrow
			classes={{ tooltip: classes.tooltip, popper: classes.tooltipPopper }}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">{title}</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const PLAN_WA_LIMITS = { basic: 1, pro: 3, full: null };

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const isAdmin = user?.profile === "admin" || user?.isSuperAdmin;
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [templatesDrawerOpen, setTemplatesDrawerOpen] = useState(false);
	const [templatesWhatsApp, setTemplatesWhatsApp] = useState(null);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

	// feat_199: banner persistente cuando al borrar una conexión se promovió otra
	// como predeterminada. Persistido por empresa en localStorage hasta que el
	// usuario toque "Entendido".
	const defaultBannerKey = `waia_defaultChanged_co${user?.companyId || ""}`;
	const [defaultChangeBanner, setDefaultChangeBanner] = useState(() => {
		try {
			const v = localStorage.getItem(defaultBannerKey);
			return v ? JSON.parse(v) : null;
		} catch {
			return null;
		}
	});

	const dismissDefaultBanner = () => {
		try { localStorage.removeItem(defaultBannerKey); } catch { /* ignore */ }
		setDefaultChangeBanner(null);
	};

	// Connection alert banners (computed from whatsApps)
	const connectionAlerts = useMemo(() => {
		if (!whatsApps?.length) return [];
		const alerts = [];
		whatsApps.forEach(w => {
			if (w.isPrimary === false) return; // standby alerts handled via primary check
			if (w.status !== "CONNECTED") {
				if (w.linkedWhatsappId) {
					const standby = whatsApps.find(s => s.id === w.linkedWhatsappId);
					if (standby && standby.status === "CONNECTED") {
						alerts.push({
							severity: "warning",
							message: i18n.t("connections.alerts.primaryDownWithStandby", { name: w.name, standby: standby.name }),
						});
					} else {
						const standbyName = standby?.name || "—";
						alerts.push({
							severity: "error",
							message: i18n.t("connections.alerts.primaryDownBothFailed", { name: w.name, standby: standbyName }),
						});
					}
				} else {
					alerts.push({
						severity: "error",
						message: i18n.t("connections.alerts.primaryDown", { name: w.name }),
					});
				}
			} else if (w.linkedWhatsappId) {
				// Primary is CONNECTED — check if standby is down
				const standby = whatsApps.find(s => s.id === w.linkedWhatsappId && s.isPrimary === false);
				if (standby && standby.status !== "CONNECTED") {
					alerts.push({
						severity: "warning",
						message: i18n.t("connections.alerts.standbyDown", { name: standby.name }),
					});
				}
			}
		});
		return alerts;
	}, [whatsApps]);

	const handleStartWhatsAppSession = async whatsAppId => {
		try { await api.post(`/whatsappsession/${whatsAppId}`); }
		catch (err) { toastError(err); }
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try { await api.put(`/whatsappsession/${whatsAppId}`); }
		catch (err) { toastError(err); }
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, []);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, []);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsApp) => {
		const id = typeof whatsApp === "object" ? whatsApp.id : whatsApp;
		const name = typeof whatsApp === "object" ? whatsApp.name : "";
		const isMetaConn = typeof whatsApp === "object" && isMeta(whatsApp);

		if (action === "disconnect") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				// Meta connections have no QR — use a QR-free confirmation message.
				message: isMetaConn
					? i18n.t("connections.confirmationModal.disconnectMessageMeta")
					: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: id,
			});
		} else if (action === "delete") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: id,
			});
		} else if (action === "pause") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.outboundPause.confirmTitle", { name }),
				message: i18n.t("connections.outboundPause.confirmMessage"),
				whatsAppId: id,
				whatsAppName: name,
			});
		} else if (action === "restart") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.restartTitle"),
				message: isMetaConn
					? `${i18n.t("connections.confirmationModal.restartMessageMeta")} (${name})`
					: `${i18n.t("connections.confirmationModal.restartMessage")} (${name})`,
				whatsAppId: id,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try { await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`); }
			catch (err) { toastError(err); }
		}
		if (confirmModalInfo.action === "delete") {
			try {
				const { data } = await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
				// feat_199: si el borrado promovió otra conexión a predeterminada,
				// avisar con toast + banner persistente.
				if (data?.newDefault) {
					toast.info(i18n.t("connections.toasts.newDefault", { name: data.newDefault.name }));
					try { localStorage.setItem(defaultBannerKey, JSON.stringify(data.newDefault)); } catch { /* ignore */ }
					setDefaultChangeBanner(data.newDefault);
				}
			} catch (err) { toastError(err); }
		}
		if (confirmModalInfo.action === "pause") {
			await setOutboundPause(confirmModalInfo.whatsAppId, confirmModalInfo.whatsAppName, true);
		}
		if (confirmModalInfo.action === "restart") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/restart`);
				toast.success(i18n.t("connections.toasts.restarted"));
			} catch (err) { toastError(err); }
		}
		setConfirmModalInfo(confirmationModalInitialState);
	};

	const isMeta = (w) => w.provider === "meta" || w.provider === "meta_coexistence";

	// El estado nuevo llega por el socket `whatsapp` (WhatsAppsContext).
	const setOutboundPause = async (whatsAppId, name, paused) => {
		try {
			await api.put(`/whatsapp/${whatsAppId}/outbound-pause`, { paused });
			if (paused) toast.error(i18n.t("connections.outboundPause.toastPaused", { name }));
			else toast.success(i18n.t("connections.outboundPause.toastResumed", { name }));
		} catch (err) { toastError(err); }
	};

	// Pausar pide confirmación; reanudar es directo.
	const handleToggleOutbound = (whatsApp) => {
		if (whatsApp.outboundPaused) setOutboundPause(whatsApp.id, whatsApp.name, false);
		else handleOpenConfirmationModal("pause", whatsApp);
	};

	const openTemplates = (whatsApp) => {
		setTemplatesWhatsApp(whatsApp);
		setTemplatesDrawerOpen(true);
	};

	const renderActionButtons = whatsApp => (
		<>
			{/* QR code buttons — only for wwebjs/whaileys */}
			{whatsApp.status === "qrcode" && !isMeta(whatsApp) && (
				<Button size="small" variant="contained" color="primary"
					onClick={() => handleOpenQrModal(whatsApp)}>
					{i18n.t("connections.buttons.qrcode")}
				</Button>
			)}
			{whatsApp.status === "DISCONNECTED" && !isMeta(whatsApp) && (
				<>
					<Button size="small" variant="outlined" color="primary"
						onClick={() => handleStartWhatsAppSession(whatsApp.id)}>
						{i18n.t("connections.buttons.tryAgain")}
					</Button>{" "}
					<Button size="small" variant="outlined" color="secondary"
						onClick={() => handleRequestNewQrCode(whatsApp.id)}>
						{i18n.t("connections.buttons.newQr")}
					</Button>
				</>
			)}
			{/* Meta: show REINTENTAR when not connected and not opening */}
			{isMeta(whatsApp) && whatsApp.status !== "CONNECTED" && whatsApp.status !== "OPENING" && (
				<Button size="small" variant="outlined" color="primary"
					onClick={() => handleOpenConfirmationModal("restart", whatsApp)}>
					{i18n.t("connections.buttons.tryAgain")}
				</Button>
			)}
			{(whatsApp.status === "CONNECTED" || whatsApp.status === "PAIRING" || whatsApp.status === "TIMEOUT") && (
				<Button size="small" variant="outlined" color="secondary"
					onClick={() => handleOpenConfirmationModal("disconnect", whatsApp)}>
					{i18n.t("connections.buttons.disconnect")}
				</Button>
			)}
			{whatsApp.status === "OPENING" && (
				<Button size="small" variant="outlined" disabled color="default">
					{i18n.t("connections.buttons.connecting")}
				</Button>
			)}
		</>
	);

	const renderStatusIcon = whatsApp => (
		<div className={classes.customTableCell}>
			{whatsApp.status === "DISCONNECTED" && (
				<CustomToolTip
					title={i18n.t(isMeta(whatsApp)
						? "connections.toolTips.disconnectedMeta.title"
						: "connections.toolTips.disconnected.title")}
					content={i18n.t(isMeta(whatsApp)
						? "connections.toolTips.disconnectedMeta.content"
						: "connections.toolTips.disconnected.content")}>
					<SignalCellularConnectedNoInternet0Bar color="secondary" />
				</CustomToolTip>
			)}
			{whatsApp.status === "OPENING" && (
				<CircularProgress size={24} className={classes.buttonProgress} />
			)}
			{whatsApp.status === "qrcode" && !isMeta(whatsApp) && (
				<CustomToolTip title={i18n.t("connections.toolTips.qrcode.title")}
					content={i18n.t("connections.toolTips.qrcode.content")}>
					<CropFree />
				</CustomToolTip>
			)}
			{whatsApp.status === "CONNECTED" && (
				<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
					<SignalCellular4Bar style={{ color: green[500] }} />
				</CustomToolTip>
			)}
			{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
				<CustomToolTip title={i18n.t("connections.toolTips.timeout.title")}
					content={i18n.t("connections.toolTips.timeout.content")}>
					<SignalCellularConnectedNoInternet2Bar color="secondary" />
				</CustomToolTip>
			)}
		</div>
	);

	const renderBackupCell = whatsApp => {
		if (!whatsApp.linkedWhatsappId) return <span style={{ color: "#9ca3af" }}>—</span>;

		if (whatsApp.isPrimary === false) {
			// This is a standby — show primary name
			const primary = whatsApps?.find(w => w.id === whatsApp.linkedWhatsappId);
			return (
				<Typography variant="caption" style={{ color: "#6b7280", fontStyle: "italic" }}>
					{i18n.t("connections.backupOf")} {primary?.name || `#${whatsApp.linkedWhatsappId}`}
				</Typography>
			);
		}

		// This is a primary — show standby info
		const standby = whatsApps?.find(w => w.id === whatsApp.linkedWhatsappId);
		if (!standby) return <span style={{ color: "#9ca3af" }}>—</span>;

		const isFailover = standby.status === "CONNECTED" && whatsApp.status !== "CONNECTED";
		const dotColor = standby.status === "CONNECTED" ? green[500] : "#ef4444";

		return (
			<span style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
				<span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor, display: "inline-block" }} />
				<Typography variant="caption">{standby.name}</Typography>
				{isFailover && (
					<Chip
						label={i18n.t("connections.badges.failover")}
						size="small"
						style={{ backgroundColor: "#f59e0b", color: "#fff", fontWeight: 700, fontSize: "0.6rem", height: 16 }}
					/>
				)}
			</span>
		);
	};

	return (
		<MainContainer>
			<MetaTemplatesDrawer
				open={templatesDrawerOpen}
				onClose={() => setTemplatesDrawerOpen(false)}
				whatsapp={templatesWhatsApp}
			/>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					{isAdmin && (() => {
						const company = user?.company;
						const plan = company?.plan || "basic";
						const maxWA = company?.maxWhatsapps != null ? company.maxWhatsapps : (PLAN_WA_LIMITS[plan] ?? null);
						const count = whatsApps?.length || 0;
						const atLimit = maxWA !== null && count >= maxWA;
						const nearLimit = maxWA !== null && count >= maxWA * 0.8;
						const chipColor = atLimit ? "#ef4444" : nearLimit ? "#f59e0b" : "#10b981";
						const chipLabel = maxWA !== null ? `Conexiones: ${count} / ${maxWA}` : `Conexiones: ${count}`;
						return (
							<>
								<Chip
									label={chipLabel}
									size="small"
									style={{ backgroundColor: chipColor, color: "#fff", fontWeight: 600, marginRight: 8 }}
								/>
								<Tooltip title={atLimit ? "Límite de conexiones alcanzado. Contactá al administrador." : ""}>
									<span>
										<Button variant="contained" color="primary"
											onClick={handleOpenWhatsAppModal} disabled={atLimit}>
											{i18n.t("connections.buttons.add")}
										</Button>
									</span>
								</Tooltip>
							</>
						);
					})()}
				</MainHeaderButtonsWrapper>
			</MainHeader>

			{/* Connection alert banners */}
			<Box px={1} pb={0}>
				{connectionAlerts.map((alert, idx) => (
					<Paper
						key={idx}
						elevation={0}
						className={classes.alertBanner}
						style={{
							backgroundColor: alert.severity === "error" ? "#fef2f2" : "#fffbeb",
							borderLeft: `4px solid ${alert.severity === "error" ? "#ef4444" : "#f59e0b"}`,
						}}
					>
						{alert.severity === "error"
							? <Warning style={{ color: "#ef4444", fontSize: 18 }} />
							: <Info style={{ color: "#f59e0b", fontSize: 18 }} />}
						<Typography variant="body2" style={{ color: alert.severity === "error" ? "#991b1b" : "#92400e" }}>
							{alert.message}
						</Typography>
					</Paper>
				))}
			</Box>

			{/* feat_199: banner de cambio de conexión predeterminada */}
			{defaultChangeBanner && (
				<Box px={1} pb={0}>
					<Paper
						elevation={0}
						className={classes.alertBanner}
						style={{ backgroundColor: "#f0fdf4", borderLeft: "4px solid #22c55e" }}
					>
						<Info style={{ color: "#22c55e", fontSize: 18 }} />
						<Typography variant="body2" style={{ color: "#166534", flex: 1 }}>
							{i18n.t("connections.defaultChanged.message", { name: defaultChangeBanner.name })}
						</Typography>
						<Button
							size="small"
							onClick={() => {
								const w = whatsApps.find(x => x.id === defaultChangeBanner.id);
								if (w) handleEditWhatsApp(w);
							}}
							style={{ color: "#166534" }}
						>
							{i18n.t("connections.defaultChanged.change")}
						</Button>
						<Button
							size="small"
							onClick={dismissDefaultBanner}
							style={{ color: "#166534", fontWeight: 600 }}
						>
							{i18n.t("connections.defaultChanged.ack")}
						</Button>
					</Paper>
				</Box>
			)}

			<div className={classes.cardsWrap}>
				{loading ? (
					<CircularProgress className={classes.buttonProgress} />
				) : whatsApps?.length > 0 ? (
					whatsApps.map(whatsApp => (
						<Paper
							key={whatsApp.id}
							variant="outlined"
							className={`${classes.card} ${whatsApp.outboundPaused ? classes.cardPaused : ""}`}
						>
							<div className={classes.cardHeader}>
								<div className={classes.headerLeft}>
									{renderStatusIcon(whatsApp)}
									<div style={{ minWidth: 0 }}>
										<div className={classes.nameRow}>
											<Typography className={classes.cardName}>{whatsApp.name}</Typography>
											{getTypeChip(whatsApp.provider)}
											{whatsApp.outboundPaused && (
												<Chip
													size="small"
													icon={<PauseCircleFilled style={{ color: "#fff" }} />}
													label={i18n.t("connections.outboundPause.chip")}
													className={classes.pausedChip}
												/>
											)}
											{whatsApp.isDefault && whatsApp.isPrimary !== false && (
												<Tooltip title={i18n.t("connections.table.default")}>
													<CheckCircle style={{ color: green[500], fontSize: 20 }} />
												</Tooltip>
											)}
										</div>
										<Typography className={classes.cardSub}>
											<span style={{ fontFamily: "monospace" }}>
												{whatsApp.number || whatsApp.phoneNumber || "—"}
											</span>
											{"  ·  "}
											{i18n.t("connections.table.lastUpdate")}: {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
										</Typography>
									</div>
								</div>
								{whatsApp.linkedWhatsappId && (
									<div>{renderBackupCell(whatsApp)}</div>
								)}
							</div>

							{whatsApp.outboundPaused && (
								<div className={classes.pausedStrip}>
									<PauseCircleFilled />
									<span>{i18n.t("connections.outboundPause.pausedHelp")}</span>
								</div>
							)}

							<div className={classes.cardActions}>
								{renderActionButtons(whatsApp)}
								{isMeta(whatsApp) && whatsApp.status === "CONNECTED" && (
									<Button
										size="small"
										variant="contained"
										startIcon={<Description style={{ fontSize: 16 }} />}
										style={{ background: "#25d366", color: "#fff", fontWeight: 700 }}
										onClick={() => openTemplates(whatsApp)}
									>
										{i18n.t("connections.buttons.templates")}
									</Button>
								)}
								<span style={{ flex: 1 }} />
								{isAdmin && (
									<div className={classes.outboundToggle}>
										<Typography className={classes.outboundLabel}>
											{i18n.t("connections.outboundPause.switchLabel")}:
										</Typography>
										<Switch
											size="small"
											checked={!whatsApp.outboundPaused}
											onChange={() => handleToggleOutbound(whatsApp)}
											inputProps={{ "aria-label": i18n.t("connections.outboundPause.switchLabel") }}
										/>
										<Typography
											className={classes.outboundLabel}
											style={{ color: whatsApp.outboundPaused ? "#dc2626" : green[700] }}
										>
											{whatsApp.outboundPaused
												? i18n.t("connections.outboundPause.paused")
												: i18n.t("connections.outboundPause.active")}
										</Typography>
									</div>
								)}
								{isAdmin && (
									<>
										<Tooltip title={i18n.t("connections.buttons.edit")}>
											<IconButton size="small" onClick={() => handleEditWhatsApp(whatsApp)}>
												<Edit />
											</IconButton>
										</Tooltip>
										<Tooltip title={i18n.t("connections.buttons.restart")}>
											<IconButton size="small" onClick={() => handleOpenConfirmationModal("restart", whatsApp)}>
												<Refresh />
											</IconButton>
										</Tooltip>
										<Tooltip title={i18n.t("connections.buttons.delete")}>
											<IconButton size="small" onClick={() => handleOpenConfirmationModal("delete", whatsApp)}>
												<DeleteOutline />
											</IconButton>
										</Tooltip>
									</>
								)}
							</div>

							{isMeta(whatsApp) && (
								<MetaHealthBanner whatsApp={whatsApp} onCreateTemplate={() => openTemplates(whatsApp)} />
							)}
						</Paper>
					))
				) : (
					<Typography className={classes.emptyState}>
						{i18n.t("connections.empty")}
					</Typography>
				)}
			</div>
		</MainContainer>
	);
};

export default Connections;
